import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type LogisticsRole = "dispatcher" | "broker" | "carrier" | null;

/**
 * The current user's logistics_role, read from their company row.
 *
 * Mirrors the inline read RoleGate does, centralised so pages can tailor copy
 * and defaults to the viewer's role (e.g. a carrier vets a broker, a dispatcher
 * vets a carrier). Falls back to null while loading or when no company resolves,
 * so callers should treat null as "unknown role, use the neutral default".
 */
export function useLogisticsRole() {
  const { companyId } = useAuth();
  return useQuery({
    queryKey: ["logistics-role", companyId],
    enabled: !!companyId,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<LogisticsRole> => {
      if (!companyId) return null;
      const { data } = await supabase
        .from("companies")
        .select("logistics_role")
        .eq("id", companyId)
        .maybeSingle();
      return (data?.logistics_role ?? null) as LogisticsRole;
    },
  });
}
